// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useTrailDropOff } from '../../hooks/useTrailDropOff';
import { useCompanyDataYearRange } from '../../hooks/useCompanyDataYearRange';
import { formatCurrency } from '../../utils/formatters';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { alertTypeLabel } from '../../utils/alertTypeLabel';
import { TrendingDown, HeartPulse, AlertTriangle, Siren, ArrowDownToLine, UserMinus, ChevronsDown, Sprout, ChevronDown, ChevronRight, History, RotateCcw } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';
import StatCard from '../../components/common/StatCard';
import { Pagination } from '../../components/common/Pagination';
import { useTrailRunOffPercentage } from '../../hooks/useTrailRunOffPercentage';
import TrailBookChangeStatCard from '../../components/reports/TrailBookChangeStatCard';
import { cardHelp } from '../../config/helpContent';
import { useLoanContacts } from '../../hooks/useLoanContacts';
import ContactTimeline from '../../components/contacts/ContactTimeline';

// Sort field type for table
type SortField = 'alertType' | 'customerName' | 'brokerDisplay' | 'paymentMonth' | 'trailCents' | 'variancePercent' | 'lender';
type SortDirection = 'asc' | 'desc';

interface Office {
  id: string;
  name: string;
}

interface OutletContextType {
  userData: any;
  companyData: any;
  userVisibleOffices: Office[];
  userVisibleTemplates: any[];
  loanBooks: ResolvedLoanBook[];
}

// Fallback names for templates missing the 'name' field in Firestore
const TEMPLATE_NAME_FALLBACKS: Record<string, string> = {
  'RISjXTNkYREXdzFwN2L7': 'Loan Market',
  'e6PK4ikHYEhUqnhu2UdL': 'Entossey',
  'mCeqUyuWfGWZmDUbu1YB': 'Entical',
  'Fa3vNzL234xK1DJuuXRP': 'Entover',
};

// Clean up template names - remove unnecessary suffixes
const cleanTemplateName = (name: string): string => {
  return name
    .replace(/\s*\(LMG\)\s*/gi, '')
    .replace(/\s*Commission File\s*/gi, '')
    .trim();
};

/**
 * TrailDropOffPage - Detect and analyze trail commission anomalies
 *
 * Color-coded alert system:
 * - RED: Current Month Drop-Off (trail went to $0 this month)
 * - BLUE: Previous Months Drop-Off (no trail for 2+ months)
 * - YELLOW: Significant Variance (>20% drop from prior month)
 * - GREEN: 2nd Year Trail Commencement (new trail after 12+ months)
 */

const TrailDropOffPage: React.FC = () => {
  const { userData, userVisibleOffices, userVisibleTemplates, loanBooks = [] } = useOutletContext<OutletContextType>();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkLoanId = searchParams.get('loanId');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());

  // ✅ DYNAMIC YEAR RANGE: Fetch from BigQuery for year dropdown population
  const { earliestYear, latestYear } = useCompanyDataYearRange({
    companyId: userData?.companyId,
  });
  const [yearType, setYearType] = useState<'calendar' | 'financial'>('calendar');
  const [selectedBroker, setSelectedBroker] = useState('all');
  const [alertTypeFilter, setAlertTypeFilter] = useState<'all' | 'current_month_dropoff' | 'previous_months_dropoff' | 'significant_variance' | 'second_year_commencement'>('all');

  // New filter state
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState('all');

  const resolvedParams = useMemo(() =>
      resolveLoanBookSelection(selectedTemplate, loanBooks),
      [selectedTemplate, loanBooks]
  );

  const [monthFilter, setMonthFilter] = useState<string>('all');
  const [fileTemplates, setFileTemplates] = useState<Array<{id: string, name: string}>>([]);

  // Chart type toggle
  const [chartType, setChartType] = useState<'area' | 'bar'>('bar');

  // Table sort state
  const [sortField, setSortField] = useState<SortField>('paymentMonth');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Contact tracking state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Canonical loan-keyed contact store (dual-reads legacy trail_dropoff_contacts +
  // the other legacy collections + canonical loan_contacts; see useLoanContacts).
  const { contactsByLoan, mutateContact } = useLoanContacts(userData?.companyId);

  // Active company brokers for the contact "Made By" dropdown (same pattern as
  // AcquiredBooksPage). broker/broker_assistant → self only; other roles read the
  // company brokers subcollection. NOTE: this is distinct from `availableBrokers`
  // (the broker FILTER dropdown, derived from records) — kept separate so filter
  // behaviour is unchanged.
  const [contactBrokerList, setContactBrokerList] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!userData?.companyId) return;

    if (userData?.role === 'broker' || userData?.role === 'broker_assistant') {
      const brokerName = userData.commissionDataName || userData.displayName || '';
      if (brokerName) {
        setContactBrokerList([{ id: 'self', name: brokerName }]);
      }
      return;
    }

    const brokersQuery = query(
      collection(db, `companies/${userData.companyId}/brokers`)
    );

    const unsubscribe = onSnapshot(
      brokersQuery,
      (snapshot) => {
        const brokers: { id: string; name: string }[] = [];
        snapshot.docs.forEach((docSnap) => {
          const data = docSnap.data();
          if (data.mergedInto) return;
          if ((data.status || 'active') !== 'active') return;
          if (data.currentName && typeof data.currentName === 'string') {
            brokers.push({ id: docSnap.id, name: data.currentName });
          }
        });
        brokers.sort((a, b) => a.name.localeCompare(b.name));
        setContactBrokerList(brokers);
      },
      (err) => {
        console.error('[TrailDropOffPage] Error fetching brokers for contact dropdown:', err);
      }
    );

    return () => unsubscribe();
  }, [userData?.companyId, userData?.role, userData?.commissionDataName, userData?.displayName]);

  // Deep-link state
  const deepLinkRowRef = useRef<HTMLTableRowElement | null>(null);
  const deepLinkProcessed = useRef(false);
  const [highlightedLoanId, setHighlightedLoanId] = useState<string | null>(null);
  const [scrollTrigger, setScrollTrigger] = useState(0);

  // Generate year options for dropdown (newest first)
  // ✅ FIX (2026-01-05): Always include current year even if no data exists yet
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const dynamicLatestYear = Math.max(latestYear ?? currentYear, currentYear);
    const dynamicEarliestYear = earliestYear ?? (currentYear - 4);
    const years = [];
    for (let year = dynamicLatestYear; year >= dynamicEarliestYear; year--) {
      years.push(year);
    }
    return years;
  }, [earliestYear, latestYear]);

  // Calculate date range based on year type
  // Australian Financial Year: FY2024 = July 2023 to June 2024
  const { startMonth, endMonth } = useMemo(() => {
    if (yearType === 'financial') {
      return {
        startMonth: `${yearFilter - 1}-07`,  // FY2024 starts July 2023
        endMonth: `${yearFilter}-06`,         // FY2024 ends June 2024
      };
    }
    return {
      startMonth: `${yearFilter}-01`,
      endMonth: `${yearFilter}-12`,
    };
  }, [yearFilter, yearType]);

  // Templates from centralized context (no per-page Firestore query)
  useEffect(() => {
    const templates = (userVisibleTemplates || []).map((t: any) => ({
      id: t.id,
      name: cleanTemplateName(t.name || t.templateName || TEMPLATE_NAME_FALLBACKS[t.id] || t.id),
    }));
    setFileTemplates(templates.sort((a, b) => a.name.localeCompare(b.name)));
  }, [userVisibleTemplates]);

  // Smart month filter defaults
  // Current year -> "All Months", Historical year -> last month of period
  useEffect(() => {
    const now = new Date();
    const thisYear = now.getFullYear();

    if (yearType === 'calendar') {
      // Historical calendar year -> default to December
      setMonthFilter(yearFilter < thisYear ? '12' : 'all');
    } else {
      // Financial year: FY2025 = Jul 2024 - Jun 2025
      const fyEndDate = new Date(yearFilter, 5, 30); // June 30 of FY end year
      // Historical FY -> default to June
      setMonthFilter(now > fyEndDate ? '06' : 'all');
    }
  }, [yearFilter, yearType]);

  // Fetch trail drop-off data from BigQuery
  const {
    records,
    monthlyTrend,
    loading,
    error,
    totalAlerts,
    currentMonthDropOffs,
    previousMonthsDropOffs,
    significantVariances,
    secondYearCommencements,
    historicalDropOffs,
    recoveredDropOffs,
    openAlertCount,
    returnedRecordCount,
    recordsTruncated,
    dataWindows,
    runCompleteness,
  } = useTrailDropOff({
    companyId: userData?.companyId || '',
    startMonth,
    endMonth,
    officeId: selectedOffice === 'all' ? undefined : selectedOffice,
    brokerId: selectedBroker === 'all' ? undefined : selectedBroker,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
    alertType: alertTypeFilter,
  });

  // Trail run-off percentage (Aaron Christie-David benchmark: < 8% healthy, 8-12% warning, > 12% critical)
  // Scoped with the SAME office / broker / loan-book params as useTrailDropOff
  // above, so the card agrees with the sibling cards. It previously took only
  // companyId and blended every loan book whatever the filter said.
  const {
    runOffPercentage,
    health: runOffHealth,
    bookChange: runOffBookChange,
    periodLabel: runOffPeriodLabel,
    loading: runOffLoading,
  } = useTrailRunOffPercentage({
    companyId: userData?.companyId || '',
    officeId: selectedOffice === 'all' ? undefined : selectedOffice,
    brokerId: selectedBroker === 'all' ? undefined : selectedBroker,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
  });

  // Compute human-readable period label for stat cards (e.g., "Jan – Dec 2026" or "Jul 2025 – Jun 2026")
  const periodLabel = useMemo(() => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const [startY, startM] = startMonth.split('-').map(Number);
    const [endY, endM] = endMonth.split('-').map(Number);
    if (startY === endY) {
      return `${monthNames[startM - 1]} – ${monthNames[endM - 1]} ${endY}`;
    }
    return `${monthNames[startM - 1]} ${startY} – ${monthNames[endM - 1]} ${endY}`;
  }, [startMonth, endMonth]);

  // Compute run completeness warning for the report banner
  const incompleteTemplateWarnings = useMemo(() => {
    const incomplete = runCompleteness.filter(
      (rc) => rc.runStrategy === 'sequential' && !rc.isComplete
    );
    if (incomplete.length === 0) return null;
    // Build per-template warning strings
    const templateNames: Record<string, string> = {
      'RISjXTNkYREXdzFwN2L7': 'Loan Market',
      'e6PK4ikHYEhUqnhu2UdL': 'Entossey',
      'mCeqUyuWfGWZmDUbu1YB': 'Entical',
      'Fa3vNzL234xK1DJuuXRP': 'Entover',
      'kgygYJRuz9ON56labRDT': 'Cranbourne (AFG)',
    };
    return incomplete.map((rc) => {
      const name = templateNames[rc.templateId] || fileTemplates.find(t => t.id === rc.templateId)?.name || rc.templateId;
      const missing = [];
      for (let r = 1; r <= rc.expectedRunCount; r++) {
        if (!rc.availableRuns.includes(r)) missing.push(r);
      }
      return `${name}: Run ${missing.join(' & ')} pending`;
    });
  }, [runCompleteness, fileTemplates]);

  // Get unique brokers for filter dropdown (sorted alphabetically)
  const availableBrokers = useMemo(() => {
    const brokerMap = new Map();
    records.forEach(r => {
      if (!brokerMap.has(r.brokerId)) {
        brokerMap.set(r.brokerId, r.brokerDisplay);
      }
    });
    return Array.from(brokerMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [records]);

  // Transform monthly trend for chart
  // Fill in all months for calendar/financial year to ensure complete X-axis
  const chartData = useMemo(() => {
    // Create a map of existing data
    const dataMap = new Map<string, typeof monthlyTrend[0]>();
    monthlyTrend.forEach(m => dataMap.set(m.month, m));

    // Generate all months for the selected year range
    const months: string[] = [];
    const [startY, startM] = startMonth.split('-').map(Number);
    const [endY, endM] = endMonth.split('-').map(Number);

    let year = startY;
    let month = startM;
    while (year < endY || (year === endY && month <= endM)) {
      months.push(`${year}-${String(month).padStart(2, '0')}`);
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }

    return months.map(m => {
      const existing = dataMap.get(m);
      return {
        month: m,
        currentDropOffs: existing?.currentMonthDropOffs || 0,
        previousDropOffs: existing?.previousMonthsDropOffs || 0,
        variances: existing?.significantVariances || 0,
        commencements: existing?.secondYearCommencements || 0,
        historical: existing?.historicalDropOffs || 0,
        // Grouped by the month the drop-off STARTED, not the month it resumed.
        resumed: existing?.recoveredDropOffs || 0,
      };
    });
  }, [monthlyTrend, startMonth, endMonth]);

  // Format month for display (2025-02 -> Feb)
  const formatMonthLabel = useCallback((month: string) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const [, monthNum] = month.split('-').map(Number);
    return monthNames[monthNum - 1] || month;
  }, []);

  // Sort handler
  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      // Default to asc for text fields, desc for numeric/date fields
      setSortDirection(field === 'customerName' || field === 'brokerDisplay' || field === 'lender' ? 'asc' : 'desc');
    }
  }, [sortField]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [yearFilter, yearType, selectedOffice, selectedBroker, selectedTemplate, monthFilter, alertTypeFilter, sortField, sortDirection]);

  // Row expansion toggle
  const toggleRow = useCallback((loanId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(loanId)) {
        newSet.delete(loanId);
      } else {
        newSet.add(loanId);
      }
      return newSet;
    });
  }, []);

  // Loan-book filter is applied server-side via templateId/entityCode in
  // useTrailDropOff (see resolveLoanBookSelection at line 109). `records` is
  // already scoped — the previous client-side re-filter compared the raw
  // dropdown value against record.templateId, which mismatched for unified-mode
  // entity-grouped tabs ('entity:LOAN_MARKET' never equals a real templateId)
  // and emptied the Alert Details table for Vision / Loan Market subscribers.
  const monthFilteredRecords = useMemo(() => {
    if (monthFilter === 'all') return records;
    return records.filter(r => r.paymentMonth.endsWith(`-${monthFilter}`));
  }, [records, monthFilter]);

  // Sort filtered records
  const sortedRecords = useMemo(() => {
    return [...monthFilteredRecords].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // String comparison for text fields
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [monthFilteredRecords, sortField, sortDirection]);

  // Sort indicator component
  const SortIcon = useCallback(({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-500">↕</span>;
    }
    return <span className="ml-1 text-blue-400">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  }, [sortField, sortDirection]);

  // Deep-link: navigate to correct page, expand row, and scroll into view
  useEffect(() => {
    if (!deepLinkLoanId || loading || sortedRecords.length === 0 || deepLinkProcessed.current) return;

    const targetIndex = sortedRecords.findIndex(r => r.loanId === deepLinkLoanId);
    if (targetIndex === -1) {
      // Loan not found in current results — clear param silently
      setSearchParams({}, { replace: true });
      deepLinkProcessed.current = true;
      return;
    }

    // Jump to correct page
    const targetPage = Math.floor(targetIndex / pageSize) + 1;
    setCurrentPage(targetPage);

    // Expand the target row and highlight it
    setExpandedRows(prev => new Set(prev).add(deepLinkLoanId));
    setHighlightedLoanId(deepLinkLoanId);

    // Clear URL param and trigger scroll
    setSearchParams({}, { replace: true });
    deepLinkProcessed.current = true;
    setScrollTrigger(prev => prev + 1);
  }, [deepLinkLoanId, loading, sortedRecords, pageSize, setSearchParams]);

  // Deep-link: scroll to target row after render
  useEffect(() => {
    if (scrollTrigger === 0) return;
    // Delay to allow DOM to render after page change
    const timer = setTimeout(() => {
      deepLinkRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
    return () => clearTimeout(timer);
  }, [scrollTrigger]);

  // Month options for dropdown
  const monthOptions = useMemo(() => {
    if (yearType === 'calendar') {
      return ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    }
    // Financial year starts in July
    return ['07', '08', '09', '10', '11', '12', '01', '02', '03', '04', '05', '06'];
  }, [yearType]);

  // Alert badge styles
  const getAlertBadge = useCallback((alertType: string | null) => {
    switch (alertType) {
      case 'current_month_dropoff':
        return 'bg-red-900 text-red-300 border border-red-700';
      case 'previous_months_dropoff':
        return 'bg-blue-900 text-blue-300 border border-blue-700';
      case 'significant_variance':
        return 'bg-yellow-900 text-yellow-300 border border-yellow-700';
      case 'second_year_commencement':
        return 'bg-green-900 text-green-300 border border-green-700';
      case 'historical_dropoff':
        return 'bg-amber-900 text-amber-300 border border-amber-700';
      case 'recovered_dropoff':
        return 'bg-slate-700 text-slate-300 border border-slate-600';
      default:
        return 'bg-gray-700 text-gray-300';
    }
  }, []);

  // Curated copy lives in alertTypeLabel(); anything it does not know is derived
  // from the tier's own name rather than rendered as "Unknown". The backend can emit
  // a tier before this build knows it, because functions and hosting deploy in
  // separate steps, and a broker reading "Unknown" on a commission report takes it
  // for a data fault rather than a UI gap. Prod, 2026-08-25.
  const getAlertLabel = useCallback((alertType: string | null) => alertTypeLabel(alertType), []);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      // Format month for display (e.g., "2025-02" -> "February 2025")
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const [year, monthNum] = data.month.split('-').map(Number);
      const formattedMonth = `${monthNames[monthNum - 1]} ${year}`;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl border border-gray-700">
          <p className="font-bold text-sm mb-2">{formattedMonth}</p>
          <p className="text-red-400 text-xs">Current drop-offs: {data.currentDropOffs}</p>
          <p className="text-indigo-400 text-xs">Multi-month trail gaps: {data.previousDropOffs}</p>
          <p className="text-orange-400 text-xs">Earlier unresolved: {data.historical}</p>
          <p className="text-slate-300 text-xs">Drop-offs later resumed: {data.resumed}</p>
          <p className="text-yellow-400 text-xs">Significant drops: {data.variances}</p>
          <p className="text-green-400 text-xs">2nd year starts: {data.commencements}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return <SkeletonReportPage statCards={6} statColumns={3} tableColumns={10} />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
          <h3 className="text-red-300 font-semibold mb-2">Error Loading Data</h3>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={TrendingDown}
        title="Loan Trail Drop-Off"
        subtitle="Monitor trail commission anomalies and opportunities"
      />

      {/* Incomplete data warning banner */}
      {incompleteTemplateWarnings && incompleteTemplateWarnings.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg px-4 py-3 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-400/90">
            <span className="font-medium">Incomplete data:</span>{' '}
            {incompleteTemplateWarnings.join('. ')}. Alert counts reflect available runs only, and the comparison is run-matched.
            {dataWindows.length > 1 && (
              <>
                {' '}Templates are current to different months
                {' '}({dataWindows
                  .filter(w => w.knowledgeCutoffMonth)
                  .map(w => w.knowledgeCutoffMonth)
                  .filter((m, i, a) => a.indexOf(m) === i)
                  .sort()
                  .join(', ')}), and each is compared against its own latest month.
              </>
            )}
          </div>
        </div>
      )}

      {/* Summary Cards (9): 1→2→3 across breakpoints. Three rows of three so each
          card keeps a readable width; a 4-up grid would strand one card on its own row. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard
          title="Portfolio Run-Off Rate"
          value={runOffPercentage !== null ? `${runOffPercentage.toFixed(1)}%` : '--'}
          periodLabel={runOffPeriodLabel}
          subtitle={runOffPercentage !== null ? (runOffHealth === 'critical' ? '>12% — needs attention' : runOffHealth === 'warning' ? '8–12% — monitor' : '<8% — healthy') : undefined}
          icon={runOffHealth === 'critical' ? <Siren size={24} /> : runOffHealth === 'warning' ? <AlertTriangle size={24} /> : <HeartPulse size={24} />}
          gradientColor={runOffHealth === 'critical' ? '#ef4444' : runOffHealth === 'warning' ? '#f59e0b' : '#22c55e'}
          isUpdating={runOffLoading}
          helpPageKey="trailRunOff"
          helpKey="portfolioRunOffRate"
          helpTooltip={cardHelp('trailRunOff', 'portfolioRunOffRate')}
        />
        <TrailBookChangeStatCard change={runOffBookChange} periodLabel={runOffPeriodLabel} loading={runOffLoading} />
        <StatCard
          title="Open Alerts"
          value={openAlertCount}
          periodLabel={periodLabel}
          subtitle="Excludes resumed trail"
          icon={<AlertTriangle size={24} />}
          gradientColor="#3b82f6"
        />
        <StatCard
          title="Current Drops"
          value={currentMonthDropOffs}
          periodLabel={periodLabel}
          subtitle="Latest month with data"
          icon={<ArrowDownToLine size={24} />}
          gradientColor="#ef4444"
        />
        <StatCard
          title="Multi-Month Gaps"
          value={previousMonthsDropOffs}
          periodLabel={periodLabel}
          subtitle="No trail 2+ months"
          icon={<UserMinus size={24} />}
          gradientColor="#6366f1"
        />
        <StatCard
          title="Earlier Unresolved"
          value={historicalDropOffs}
          periodLabel={periodLabel}
          subtitle="Dropped, never resumed"
          icon={<History size={24} />}
          gradientColor="#f97316"
        />
        <StatCard
          title="Trail Resumed"
          value={recoveredDropOffs}
          periodLabel={periodLabel}
          subtitle="Paid again since"
          icon={<RotateCcw size={24} />}
          gradientColor="#64748b"
        />
        <StatCard
          title="Significant Drops"
          value={significantVariances}
          periodLabel={periodLabel}
          subtitle="20%+ variance"
          icon={<ChevronsDown size={24} />}
          gradientColor="#f59e0b"
        />
        <StatCard
          title="2nd Year Starts"
          value={secondYearCommencements}
          periodLabel={periodLabel}
          subtitle="New trail activity"
          icon={<Sprout size={24} />}
          gradientColor="#22c55e"
        />
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
        <h2 className="text-lg font-semibold mb-4 text-white">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Year</label>
            <select
              value={yearFilter}
              onChange={(e) => setYearFilter(parseInt(e.target.value))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {yearType === 'financial' ? `${year - 1}/${year}` : year}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Year Type</label>
            <select
              value={yearType}
              onChange={(e) => setYearType(e.target.value as 'calendar' | 'financial')}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="calendar">Calendar Year</option>
              <option value="financial">Financial Year</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Office</label>
            <select
              value={selectedOffice}
              onChange={(e) => setSelectedOffice(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Offices</option>
              {userVisibleOffices?.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Loan Book</label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {/* getDisplayLoanBooks collapses entity-typed books by entityCode (Choice+LMG → one
                  "Loan Market") for unified-mode tenants; passes legacy template-typed books
                  through unchanged for Entourage. */}
              <option value="all">All Loan Books</option>
              {getDisplayLoanBooks(loanBooks).map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Broker</label>
            <select
              value={selectedBroker}
              onChange={(e) => setSelectedBroker(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Brokers</option>
              {availableBrokers.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Chart Type</label>
          <div className="flex gap-1 bg-gray-700 rounded-lg p-1 max-w-xs">
            <button
              onClick={() => setChartType('bar')}
              className={`flex-1 px-4 py-2 text-sm rounded-md transition-colors ${
                chartType === 'bar'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Bar Chart
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`flex-1 px-4 py-2 text-sm rounded-md transition-colors ${
                chartType === 'area'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              Area Chart
            </button>
          </div>
        </div>
      </div>

      {/* Monthly Trend Chart */}
      {chartData.length > 0 && (
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
          <div className="flex justify-between items-center p-6 pb-0">
            <h2 className="text-xl font-semibold text-white">Monthly Alert Trends</h2>
            <div className="text-sm text-gray-400">
              {yearType === 'financial' ? `FY${yearFilter - 1}/${String(yearFilter).slice(-2)}` : yearFilter}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={350}>
            {chartType === 'bar' ? (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} tickFormatter={formatMonthLabel} />
                <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                <Bar dataKey="currentDropOffs" name="Current drop-off" stackId="a" fill="#ef4444" />
                <Bar dataKey="previousDropOffs" name="Multi-month trail gap" stackId="a" fill="#6366f1" />
                <Bar dataKey="historical" name="Earlier unresolved" stackId="a" fill="#f97316" />
                <Bar dataKey="resumed" name="Drop-offs later resumed" stackId="a" fill="#64748b" />
                <Bar dataKey="variances" name="Significant Drops" stackId="a" fill="#eab308" />
                <Bar dataKey="commencements" name="2nd Year Starts" stackId="a" fill="#22c55e" />
              </BarChart>
            ) : (
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gradientRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="gradientBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="gradientYellow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#eab308" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#eab308" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="gradientGreen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="gradientOrange" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="gradientSlate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#64748b" stopOpacity={0.35}/>
                    <stop offset="95%" stopColor="#64748b" stopOpacity={0.05}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} tickFormatter={formatMonthLabel} />
                <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                <Area
                  type="monotone"
                  dataKey="currentDropOffs"
                  name="Current drop-off"
                  stroke="#ef4444"
                  fill="url(#gradientRed)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="previousDropOffs"
                  name="Multi-month trail gap"
                  stroke="#6366f1"
                  fill="url(#gradientBlue)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="historical"
                  name="Earlier unresolved"
                  stroke="#f97316"
                  fill="url(#gradientOrange)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="resumed"
                  name="Drop-offs later resumed"
                  stroke="#64748b"
                  fill="url(#gradientSlate)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="variances"
                  name="Significant Drops"
                  stroke="#eab308"
                  fill="url(#gradientYellow)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="commencements"
                  name="2nd Year Starts"
                  stroke="#22c55e"
                  fill="url(#gradientGreen)"
                  strokeWidth={2}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {/* Alert Legend */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
        <h3 className="text-lg font-semibold mb-4 text-white">Alert Types</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start space-x-3">
            <div className="bg-red-900 text-red-300 border border-red-700 px-3 py-1 rounded-full text-xs font-semibold min-w-[70px] text-center">RED</div>
            <div>
              <p className="text-sm font-medium text-white">Current drop-off</p>
              <p className="text-xs text-gray-400">Trail went to $0 in the latest month this template has data for</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-amber-900 text-amber-300 border border-amber-700 px-3 py-1 rounded-full text-xs font-semibold min-w-[70px] text-center">AMBER</div>
            <div>
              <p className="text-sm font-medium text-white">Earlier unresolved drop-off</p>
              <p className="text-xs text-gray-400">Trail went to $0 in an earlier month and has not been paid since</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-slate-700 text-slate-300 border border-slate-600 px-3 py-1 rounded-full text-xs font-semibold min-w-[70px] text-center">GREY</div>
            <div>
              <p className="text-sm font-medium text-white">Trail resumed</p>
              <p className="text-xs text-gray-400">Trail went to $0 but has since been paid again. No action needed</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-blue-900 text-blue-300 border border-blue-700 px-3 py-1 rounded-full text-xs font-semibold min-w-[70px] text-center">BLUE</div>
            <div>
              <p className="text-sm font-medium text-white">Multi-month trail gap</p>
              <p className="text-xs text-gray-400">No trail commission for 2+ months (confirmed customer churn)</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-yellow-900 text-yellow-300 border border-yellow-700 px-3 py-1 rounded-full text-xs font-semibold min-w-[70px] text-center">YELLOW</div>
            <div>
              <p className="text-sm font-medium text-white">Significant Variance</p>
              <p className="text-xs text-gray-400">Trail commission dropped by more than 20% from prior month</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="bg-green-900 text-green-300 border border-green-700 px-3 py-1 rounded-full text-xs font-semibold min-w-[70px] text-center">GREEN</div>
            <div>
              <p className="text-sm font-medium text-white">2nd Year Trail Commencement</p>
              <p className="text-xs text-gray-400">New trail commission starting after 12+ months (opportunity)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Records Table */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-700">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-white">Alert Details</h2>
              <p className="text-sm text-gray-400 mt-1">
                {sortedRecords.length} alert{sortedRecords.length !== 1 ? 's' : ''}
                {sortedRecords.length !== records.length && ` of ${records.length} total`}
                {recordsTruncated && (
                  <span className="text-amber-400">
                    {' '}— table capped at {returnedRecordCount.toLocaleString()} rows, so the cards above may count more
                  </span>
                )}
                {' '}• Click a row to expand and log contact attempts
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <select
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Months</option>
                {monthOptions.map(m => (
                  <option key={m} value={m}>{formatMonthLabel(`2025-${m}`)}</option>
                ))}
              </select>
              <select
                value={alertTypeFilter}
                onChange={(e) => setAlertTypeFilter(e.target.value as any)}
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Alerts</option>
                <option value="current_month_dropoff">Current drop-offs</option>
                <option value="previous_months_dropoff">Multi-month trail gaps</option>
                <option value="historical_dropoff">Earlier unresolved drop-offs</option>
                <option value="recovered_dropoff">Trail resumed</option>
                <option value="significant_variance">Significant Drops</option>
                <option value="second_year_commencement">2nd Year Starts</option>
              </select>
            </div>
          </div>
        </div>

        {sortedRecords.length > 0 ? (() => {
          const paginatedRecords = sortedRecords.slice((currentPage - 1) * pageSize, currentPage * pageSize);
          return (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900 border-b border-gray-700">
                <tr>
                  <th
                    onClick={() => handleSort('alertType')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Alert Type <SortIcon field="alertType" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('customerName')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Customer <SortIcon field="customerName" /></div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Loan ID
                  </th>
                  <th
                    onClick={() => handleSort('brokerDisplay')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Broker <SortIcon field="brokerDisplay" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('paymentMonth')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Month <SortIcon field="paymentMonth" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('trailCents')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Current Trail <SortIcon field="trailCents" /></div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Prior Trail
                  </th>
                  <th
                    onClick={() => handleSort('variancePercent')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Variance <SortIcon field="variancePercent" /></div>
                  </th>
                  <th
                    onClick={() => handleSort('lender')}
                    className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider cursor-pointer hover:text-white transition-colors"
                  >
                    <div className="flex items-center">Lender <SortIcon field="lender" /></div>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {paginatedRecords.map((record, idx) => {
                  const isExpanded = expandedRows.has(record.loanId);
                  const loanEntries = contactsByLoan[record.loanId] || [];
                  const hasContacts = loanEntries.length > 0;
                  const isHighlighted = highlightedLoanId === record.loanId;

                  return (
                    <React.Fragment key={`${record.loanId}-${record.paymentMonth}-${idx}`}>
                      <tr
                        ref={isHighlighted ? deepLinkRowRef : undefined}
                        className={`hover:bg-gray-700 transition-colors cursor-pointer ${
                          isExpanded ? 'bg-gray-700/50' : ''
                        } ${isHighlighted ? 'ring-1 ring-blue-500/60 bg-blue-900/20' : ''}`}
                        onClick={() => toggleRow(record.loanId)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${getAlertBadge(record.alertType)}`}>
                            {getAlertLabel(record.alertType)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-white">{record.customerName || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-mono text-gray-400">{record.loanId}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-300">{record.brokerDisplay}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-300">{record.paymentMonth}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${record.trailCents === 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {formatCurrency(record.trailCents)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-400">
                            {record.priorMonthCents !== null ? formatCurrency(record.priorMonthCents) : 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-semibold ${
                            record.variancePercent && record.variancePercent > 0 ? 'text-red-400' :
                            record.variancePercent && record.variancePercent < 0 ? 'text-green-400' :
                            'text-gray-400'
                          }`}>
                            {record.variancePercent !== null ? `${record.variancePercent > 0 ? '-' : '+'}${Math.abs(record.variancePercent).toFixed(1)}%` : 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-300">{record.lender || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRow(record.loanId);
                            }}
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                            {hasContacts && (
                              <span className="inline-flex items-center justify-center w-5 h-5 bg-green-900 text-green-300 rounded-full text-[10px] font-medium">
                                {loanEntries.length}
                              </span>
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Contact Tracking Section — canonical loan-keyed store */}
                      {isExpanded && (
                        <tr className="bg-gray-900/50">
                          <td
                            colSpan={10}
                            className="px-6 py-4"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ContactTimeline
                              companyId={userData?.companyId || ''}
                              loanId={record.loanId}
                              customerName={record.customerName}
                              loanBroker={record.brokerDisplay}
                              sourcePage="Trail Drop-Off"
                              entries={loanEntries}
                              availableBrokers={contactBrokerList}
                              currentUser={userData}
                              onMutate={mutateContact}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })() : (
          <div className="p-6 text-center text-gray-500">
            No alerts found for the selected filters
          </div>
        )}
        {sortedRecords.length > 0 && (
          <div className="px-2 py-2">
            <Pagination
              currentPage={currentPage}
              totalItems={sortedRecords.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailDropOffPage;
